import React, { useState, useContext, useEffect } from 'react';
import { Button } from '@tymate/elise';
import { MdFileDownload } from 'react-icons/md';
import { Container } from '@tymate/elise/ui';
import { Stack } from '@tymate/margaret';
import { saveAs } from 'file-saver';
import styled from 'styled-components';
import { useAppState } from '@tymate/elise/hooks';
import { Modal } from '@tymate/elise/components';
import ReportingParams from 'components/ReportingParams';
import { useReporting } from 'contexts/ReportingContext';
import { CollectionPointContext } from 'contexts';
import {
  getWasteRegisterCSV,
  getPastCollectionsCSV,
  getWorkOrdersCSV,
  getWeeklyWorkOrdersCSV,
} from 'api/reporting';
import { format } from 'date-fns';

const ExtractionCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: ${({ theme }) => theme.spacing(2)};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex: 1;
  min-width: 300px;
  max-width: 400px;
  min-height: 370px;
  display: flex;
  flex-direction: column;
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ExtractionTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(1)} 0;
  color: ${({ theme }) => theme.primary};
`;

const ExtractionDescription = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(1)} 0;
  color: ${({ theme }) => theme.textPrimary};
  flex: 1;
  font-size: 13px;
  text-align: justify;
`;

const ButtonContainer = styled.div`
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing(2)};
`;

const ExtractionText = styled.p`
  text-align: center;
  flex: 1;
  margin: 0;
  color: ${({ theme }) => theme.textPrimary};
  font-weight: bold;
  font-size: 16px;
`;

const ExtractionModal = ({ isOpen, onClose, config }) => {
  const { notify } = useAppState();
  const { reportingParams, endAtManuallyChanged, updateEndDateWithoutMarking } = useReporting();
  const { collectionPoint } = useContext(CollectionPointContext);
  const [csvLoading, setCsvLoading] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);

  useEffect(() => {
    if (isOpen && config === extractionConfigs.workOrders && !endAtManuallyChanged) {
      const lastDayOfYear = new Date(new Date().getFullYear(), 11, 31);
      updateEndDateWithoutMarking(lastDayOfYear);
    }
  }, [isOpen, config, endAtManuallyChanged, updateEndDateWithoutMarking]);

  const handleClose = () => {
    if (config === extractionConfigs.workOrders && !endAtManuallyChanged) {
      updateEndDateWithoutMarking(new Date());
    }
    onClose();
  };

  const generateFilename = (detailed) => {
    const fromDate = format(reportingParams.startAt, 'MM/yy');
    const toDate = format(reportingParams.endAt, 'MM/yy');
    const closestCommonParent =
      reportingParams.closestCommonParent || collectionPoint?.name;
    return `${detailed ? config.csvNameDetail() : config.csvName()}-${
      fromDate === toDate ? fromDate : `${fromDate}-${toDate}`
    }-${closestCommonParent}`;
  };

  const handleExtract = async (fused = true, detailed = false) => {
    try {
      if (fused) {
        setCsvLoading(true);
      } else {
        setZipLoading(true);
      }

      const getData = detailed ? config.getDataDetail : config.getData;
      const getFilename = generateFilename(detailed);

      const response = await getData({
        ...reportingParams,
        filename: getFilename,
        fused,
      });

      const headers = response.headers || {};

      if (response.status === 200) {
        notify('Extraction complète', {
          type: 'info',
        });

        const contentType =
          headers['content-type'] ||
          headers['contentType'] ||
          'text/csv;charset=utf-8';
        const filename =
          (headers['content-disposition'] || headers['contentDisposition'])
            ?.split('filename=')[1]
            ?.replace(/"/g, '') || getFilename;

        if (contentType.includes('application/zip')) {
          // Handle ZIP file
          const blob = new Blob([response.data], {
            type: 'application/zip',
          });
          saveAs(blob, filename + '.zip');
        } else {
          // Handle CSV file
          const csvData = '\uFEFF' + response.data;
          const blob = new Blob([csvData], {
            type: 'text/csv;charset=utf-8',
          });
          saveAs(blob, filename + '.csv');
        }
      } else {
        handleWarningOrError(response.status, response.data.message);
      }
    } catch (e) {
      console.error('Extraction error:', e);
      handleWarningOrError(e.response?.status, e.response?.data?.['error']);
    } finally {
      if (fused) {
        setCsvLoading(false);
      } else {
        setZipLoading(false);
      }
    }
  };

  const handleWarningOrError = (status, message) => {
    if (status === 204) {
      notify("Pas de données correspondant à l'extraction", {
        type: 'warning',
      });
    } else if (status === 400 && message === 'Too much content') {
      notify('Trop de données, affinez la recherche', {
        type: 'warning',
      });
    } else if (status === 400 && message === 'Invalid parameters') {
      notify("Paramètres de l'extraction invalides", {
        type: 'error',
      });
    } else {
      notify('Une erreur est survenue, veuillez rééssayer ultérieurement.', {
        type: 'error',
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      variant="medium"
      title={`Extraction - ${config.title}`}
      onRequestClose={handleClose}
    >
      <Stack direction="column" gutterSize={4}>
        <ReportingParams />
        {config === extractionConfigs.workOrders && (
          <Stack direction="row" gutterSize={6} alignX="center">
            <Stack direction="column" gutterSize={1}>
              <Stack
                direction="row"
                gutterSize={1}
                size="full"
                alignX="center"
                alignY="left"
                marginLeft={-0.5}
              >
                <ExtractionText>Extraire le calendrier prévisionnel</ExtractionText>
              </Stack>
              <Stack
                direction="row"
                gutterSize={1}
                size="flex"
                alignX="center"
                alignY="left"
              >
                <Button
                  variant="primary"
                  icon={<MdFileDownload />}
                  onClick={() => handleExtract(true)}
                  isLoading={csvLoading}
                  disabled={zipLoading}
                >
                  Consolidé
                </Button>
                <Button
                  variant="outline"
                  icon={<MdFileDownload />}
                  onClick={() => handleExtract(false)}
                  isLoading={zipLoading}
                  disabled={csvLoading}
                >
                  Un fichier par site
                </Button>
              </Stack>
            </Stack>
            <Stack direction="column" gutterSize={1}>
              <Stack
                direction="row"
                gutterSize={1}
                size="full"
                alignX="center"
                alignY="left"
                marginLeft={-0.5}
              >
                <ExtractionText>
                  Extraire les prestations détaillées
                </ExtractionText>
              </Stack>
              <Stack
                direction="row"
                gutterSize={1}
                size="flex"
                alignX="center"
                alignY="left"
              >
                <Button
                  variant="primary"
                  icon={<MdFileDownload />}
                  onClick={() => handleExtract(true, true)}
                  isLoading={csvLoading}
                  disabled={zipLoading}
                >
                  Consolidé
                </Button>
                <Button
                  variant="outline"
                  icon={<MdFileDownload />}
                  onClick={() => handleExtract(false, true)}
                  isLoading={zipLoading}
                  disabled={csvLoading}
                >
                  Un fichier par site
                </Button>
              </Stack>
            </Stack>
          </Stack>
        )}
        {config !== extractionConfigs.workOrders && (
          <Stack
            direction="row"
            gutterSize={1}
            size="flex"
            alignX="center"
            alignY="left"
            marginLeft={1}
          >
            <Button
              variant="primary"
              icon={<MdFileDownload />}
              onClick={() => handleExtract(true)}
              isLoading={csvLoading}
              disabled={zipLoading}
            >
              Consolidé
            </Button>
            <Button
              variant="outline"
              icon={<MdFileDownload />}
              onClick={() => handleExtract(false)}
              isLoading={zipLoading}
              disabled={csvLoading}
            >
              Un fichier par site
            </Button>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};

const extractionConfigs = {
  wasteRegister: {
    getData: getWasteRegisterCSV,
    csvName: () => 'dechets',
    title: 'Registre des déchets',
  },
  workOrders: {
    getData: getWeeklyWorkOrdersCSV,
    getDataDetail: getWorkOrdersCSV,
    csvName: () => 'planning-previsionnel',
    csvNameDetail: () => 'prestations',
    title: 'Planning des prestations',
  },
  collectedResources: {
    getData: getPastCollectionsCSV,
    csvName: () => 'suivi',
    title: 'Suivi des prestations',
  },
};

const ExtractionList = () => {
  const [activeModal, setActiveModal] = useState(null);

  const handleOpenModal = type => {
    setActiveModal(type);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
  };

  return (
    <Container variant="main">
      <Stack
        direction={{ default: 'column', tablet: 'row' }}
        gutterSize={2}
        alignX="center"
        marginTop={2}
      >
        {/* Registre des déchets */}
        <ExtractionCard>
          <CardContent>
            <ExtractionTitle>Registre des déchets</ExtractionTitle>
            <ExtractionDescription>
              Ce registre contient les éléments réglementaires utiles à la
              traçabilité de chacun de vos déchets depuis le jour de collecte
              sur votre site jusqu'à l'installation de traitement.
              <br />
              <br />
              Ces éléments sont à compiler dans votre registre des déchets et à
              conserver 3 ans.
            </ExtractionDescription>
          </CardContent>
          <ButtonContainer>
            <Button
              variant="primary"
              type="button"
              icon={<MdFileDownload />}
              onClick={() => handleOpenModal('wasteRegister')}
            >
              Extraire le registre
            </Button>
          </ButtonContainer>
        </ExtractionCard>

        {/* Planning des prestations */}
        <ExtractionCard>
          <CardContent>
            <ExtractionTitle>Planning des prestations</ExtractionTitle>
            <ExtractionDescription>
              Ce document contient le calendrier des interventions prévues pour
              un ou plusieurs sites.
              <br />
              <br />
              Deux niveaux d'extraction sont disponibles :
              <ul>
                <li>Sous forme de calendrier</li>
                <li>En liste avec le détails de vos prestations</li>
              </ul>
            </ExtractionDescription>
          </CardContent>
          <ButtonContainer>
            <Button
              variant="primary"
              type="button"
              icon={<MdFileDownload />}
              onClick={() => handleOpenModal('workOrders')}
            >
              Extraire le planning
            </Button>
          </ButtonContainer>
        </ExtractionCard>

        {/* Suivi des prestations */}
        <ExtractionCard>
          <CardContent>
            <ExtractionTitle>Suivi des prestations</ExtractionTitle>
            <ExtractionDescription>
              Ce document contient l'historique détaillé des prestations
              effectuées.
            </ExtractionDescription>
          </CardContent>
          <ButtonContainer>
            <Button
              variant="primary"
              type="button"
              icon={<MdFileDownload />}
              onClick={() => handleOpenModal('collectedResources')}
            >
              Extraire le suivi
            </Button>
          </ButtonContainer>
        </ExtractionCard>
      </Stack>

      {activeModal && (
        <ExtractionModal
          isOpen={true}
          onClose={handleCloseModal}
          type={activeModal}
          config={extractionConfigs[activeModal]}
        />
      )}
    </Container>
  );
};

export default ExtractionList;
